import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import heroImage from '../assets/hero-construction.jpg';
import imgResidential from '../assets/about/hero-dream-house.jpg';
import imgCommercial from '../assets/about/home-commercial-building.png';
import CallbackModal from '../components/CallbackModal';
import ConstructionHeroFx from '../components/ConstructionHeroFx';
import CountUpValue from '../components/CountUpValue';
import Reveal from '../components/Reveal';
import TiltCard from '../components/TiltCard';
import './Home.css';

const stats = [
  { value: '120+', label: 'Projects Completed' },
  { value: '28', label: 'Ongoing Sites' },
  { value: '1.2+', suffix: 'M sq. ft.', label: 'Built & Delivered' },
  { value: '200+', label: 'Designs Delivered' },
];

const ncrAreas = ['Delhi', 'Gurugram', 'Noida', 'Faridabad', 'Ghaziabad'];

const processSteps = [
  {
    title: 'Connect With Us',
    text: 'Share your plot, budget, and timeline. Our team responds quickly with a free technical consultation.',
  },
  {
    title: 'Define The Scope',
    text: 'We align on drawings, specifications, and execution plan so there are no surprises mid-build.',
  },
  {
    title: 'Approve & Mobilise',
    text: 'Lock designs and milestones, then we mobilise site teams, materials, and quality checkpoints.',
  },
  {
    title: 'Track Execution',
    text: 'Regular site updates, transparent billing, and disciplined coordination keep the job moving.',
  },
  {
    title: 'Handover',
    text: 'Snag-free completion with documentation so you can occupy with confidence.',
  },
];

const trustItems = [
  {
    title: 'Clear Estimates',
    text: 'Structured BOQ-style breakdowns so you understand cost drivers before work starts on site.',
  },
  {
    title: 'Quality On Site',
    text: 'Defined checks for critical stages—structure, waterproofing, finishes—before cover-up.',
  },
  {
    title: 'Timeline Focus',
    text: 'Programmes with dependencies flagged early so approvals and long-lead items do not stall you.',
  },
  {
    title: 'Single Coordination',
    text: 'One accountable project thread from our side, aligned with your architect and consultants.',
  },
  {
    title: 'NCR Experience',
    text: 'Deep experience with local norms, liaison, and execution across Delhi NCR.',
  },
  {
    title: 'After Handover Support',
    text: 'Structured snag closure and documentation for a smooth move-in.',
  },
];

export default function Home() {
  const [callbackOpen, setCallbackOpen] = useState(true);
  const reduceMotion = useReducedMotion();

  const openCallback = () => setCallbackOpen(true);

  const heroCopy = reduceMotion
    ? undefined
    : {
        initial: { opacity: 0, y: 22 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <>
      <CallbackModal open={callbackOpen} onClose={() => setCallbackOpen(false)} />

      <section className="home-hero">
        <div className="home-hero-media">
          <img
            src={heroImage}
            alt="Construction site with cranes and building framework"
            className="home-hero-photo"
          />
          <div className="home-hero-overlay" />
          <ConstructionHeroFx />
        </div>
        <div className="home-hero-inner">
          <div className="home-hero-layout page-wrap">
            <motion.div className="home-hero-copy" {...(heroCopy || {})}>
              <p className="home-hero-eyebrow">Design · Build · Deliver</p>
              <h1 className="home-hero-title">Building Landmarks That Last Generations</h1>
              <p className="home-hero-lead">
                End-to-end construction with clarity on cost, quality, and schedule—managed by an
                experienced team across Delhi NCR.
              </p>
              <div className="home-hero-actions">
                <motion.button
                  type="button"
                  className="home-hero-cta"
                  onClick={openCallback}
                  whileHover={reduceMotion ? undefined : { y: -2, scale: 1.02 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                >
                  Request a callback
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="home-stats" aria-label="Highlights">
        <div className="page-wrap home-stats-inner">
          {stats.map(({ value, suffix, label }, i) => (
            <Reveal key={label} className="home-stat" delay={i * 0.06}>
              <p className="home-stat-value">
                <CountUpValue value={value} active={!callbackOpen} />
                {suffix ? <span className="home-stat-suffix"> {suffix}</span> : null}
              </p>
              <p className="home-stat-label">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="home-services">
        <div className="page-wrap">
          <Reveal>
            <header className="home-section-head">
              <h2 className="home-section-title">Our Construction Services</h2>
              <p className="home-section-sub">
                Managed end-to-end—from drawings and approvals to execution and handover—with
                disciplined site management.
              </p>
            </header>
          </Reveal>
          <div className="home-services-grid">
            <Reveal delay={0.05}>
              <TiltCard as="article" className="home-service-tile">
                <div className="home-service-media">
                  <img src={imgResidential} alt="" />
                  <div className="home-service-scrim" />
                </div>
                <div className="home-service-body">
                  <h3 className="home-service-title">Residential Construction</h3>
                  <p className="home-service-text">
                    Homes and floors built for owners who want control, clear billing, and dependable
                    timelines.
                  </p>
                  <div className="home-service-actions home-service-actions--single">
                    <button
                      type="button"
                      className="home-service-primary home-service-primary--btn"
                      onClick={openCallback}
                    >
                      Plan My Home
                    </button>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
            <Reveal delay={0.12}>
              <TiltCard as="article" className="home-service-tile">
                <div className="home-service-media">
                  <img
                    src={imgCommercial}
                    alt="Modern commercial office building with glass windows and landscaped plaza"
                  />
                  <div className="home-service-scrim" />
                </div>
                <div className="home-service-body">
                  <h3 className="home-service-title">Commercial Construction</h3>
                  <p className="home-service-text">
                    Offices, retail shells, and structural packages delivered with coordination
                    across MEP and façade interfaces.
                  </p>
                  <div className="home-service-actions home-service-actions--single">
                    <button
                      type="button"
                      className="home-service-primary home-service-primary--btn"
                      onClick={openCallback}
                    >
                      Plan My Project
                    </button>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="home-areas">
        <div className="page-wrap home-areas-inner">
          <Reveal>
            <h2 className="home-areas-title">Serving Delhi NCR</h2>
            <p className="home-areas-sub">We execute projects across the National Capital Region.</p>
          </Reveal>
          <ul className="home-areas-pills">
            {ncrAreas.map((city, i) => (
              <Reveal key={city} as="li" delay={i * 0.05} direction="up">
                {city}
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-process">
        <div className="home-process-fx" aria-hidden="true" />
        <div className="page-wrap">
          <Reveal>
            <header className="home-section-head home-section-head--light">
              <h2 className="home-section-title">Our Construction Process</h2>
              <p className="home-section-sub">
                Plan. Build. Track. Move in—from first conversation to keys in hand.
              </p>
            </header>
          </Reveal>
          <ol className="home-process-list">
            {processSteps.map((step, i) => (
              <Reveal key={step.title} as="li" className="home-process-step" delay={i * 0.07}>
                <span className="home-process-num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="home-process-copy">
                  <h3 className="home-process-title">{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.1}>
            <p className="home-process-foot">
              <Link to="/services#work-process">See Our Detailed Work Process</Link>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="home-trust">
        <div className="page-wrap">
          <Reveal>
            <header className="home-section-head">
              <h2 className="home-section-title">Why Landmarks</h2>
              <p className="home-section-sub">
                Practical guarantees around clarity and execution—not buzzwords.
              </p>
            </header>
          </Reveal>
          <ul className="home-trust-grid">
            {trustItems.map(({ title, text }, i) => (
              <Reveal key={title} as="li" className="home-trust-card" delay={(i % 3) * 0.06}>
                <h3 className="home-trust-title">{title}</h3>
                <p className="home-trust-text">{text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-cta-band">
        <Reveal>
          <div className="page-wrap home-cta-inner">
            <div className="home-cta-copy">
              <h2 className="home-cta-title">Talk To Our Construction Advisor</h2>
              <p className="home-cta-text">
                Share your plot location, rough budget, and start date—we will outline the next
                steps and a realistic path to execution.
              </p>
            </div>
            <div className="home-cta-actions">
              <motion.button
                type="button"
                className="home-cta-primary"
                onClick={openCallback}
                whileHover={reduceMotion ? undefined : { y: -2, scale: 1.02 }}
                whileTap={reduceMotion ? undefined : { scale: 0.98 }}
              >
                Request a callback
              </motion.button>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
